import { useCallback, useEffect, useRef, useState } from 'react';

import type { UseScrollToOptions } from '.';

export function useScrollTo<T extends string = string>(options: UseScrollToOptions = {}) {
  const { threshold = 0.5, rootMargin = '0px', scrollIntoViewOptions } = options;
  const sectionRefs = useRef(new Map<T, HTMLElement>());
  const sectionRefCallbacks = useRef(new Map<T, (el: HTMLElement | null) => void>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [visibleSections, setVisibleSections] = useState<Set<T>>(new Set());

  const setSectionRef = useCallback((key: T) => {
    const existingCallback = sectionRefCallbacks.current.get(key);
    if (existingCallback) return existingCallback;

    const callback = (el: HTMLElement | null) => {
      const currentMap = sectionRefs.current;

      if (el) {
        currentMap.set(key, el);
        observerRef.current?.observe(el);
      } else {
        const existingEl = currentMap.get(key);
        if (existingEl) observerRef.current?.unobserve(existingEl);
        currentMap.delete(key);
        setVisibleSections((prev) => {
          if (!prev.has(key)) return prev;
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      }
    };

    sectionRefCallbacks.current.set(key, callback);
    return callback;
  }, []);

  const scrollTo = useCallback(
    (key: T) => {
      const node = sectionRefs.current.get(key);
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', ...scrollIntoViewOptions });
      }
    },
    [scrollIntoViewOptions]
  );

  const isActive = useCallback(
    (elementName: T): boolean => {
      return visibleSections.has(elementName);
    },
    [visibleSections]
  );

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibleSections((prev) => {
          const newSet = new Set(prev);

          entries.forEach((entry) => {
            for (const [key, element] of sectionRefs.current.entries()) {
              if (element === entry.target) {
                if (entry.isIntersecting) {
                  newSet.add(key);
                } else {
                  newSet.delete(key);
                }
                break;
              }
            }
          });

          return newSet;
        });
      },
      { threshold, rootMargin }
    );

    sectionRefs.current.forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [threshold, rootMargin]);

  return { setSectionRef, scrollTo, isActive, visibleSections };
}
